import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import ArticleIcon from "@mui/icons-material/Article";
import FacebookIcon from "@mui/icons-material/Facebook";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import QuizIcon from "@mui/icons-material/Quiz";
import StyleIcon from "@mui/icons-material/Style";
import WorkspacePremiumIcon from "@mui/icons-material/EmojiEvents";
import { Link } from "react-router-dom";

const footerGroups = [
  {
    title: "Learning",
    links: [
      { label: "Flashcards", href: "/flashcards", icon: StyleIcon },
      { label: "IELTS", href: "/ielts", icon: WorkspacePremiumIcon },
      { label: "TOEIC", href: "/test", icon: QuizIcon },
      { label: "Resources", href: "/document", icon: ArticleIcon },
    ],
  },
  {
    title: "Progress",
    links: [
      { label: "Ranking", href: "/ranking", icon: LeaderboardIcon },
      { label: "History", href: "/history", icon: WorkspacePremiumIcon },
    ],
  },
];

const socialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/daydreamer.so",
    icon: FacebookIcon,
    color: "#2387cc",
    bg: "#e8f6ff",
  },
  {
    label: "Email",
    href: "mailto:thevi16102004@gmail.com",
    icon: AlternateEmailIcon,
    color: "#35c759",
    bg: "#e8f9ed",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/le-phan-the-vi-b98842325/",
    icon: LinkedInIcon,
    color: "#6d4aff",
    bg: "#f0ecff",
  },
];

const dukeLewisLinks = [
  { label: "About", href: "https://dukelewis.com/profile" },
  { label: "Projects", href: "https://dukelewis.com/projects" },
  { label: "DukeLewis", href: "https://dukelewis.com" },
];

function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        mt: { xs: 5, md: 7 },
        width: "100%",
        maxWidth: "var(--default-layout-width)",
        px: { xs: 2, md: 3 },
        pb: 0,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          borderTop: "1.5px dotted var(--toeic-line)",
          borderRadius: 0,
          bgcolor: "transparent",
          boxShadow: "none",
          backdropFilter: "none",
          px: 0,
          pt: { xs: 3, md: 4 },
          pb: { xs: 2.5, md: 3 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1.25fr repeat(2, minmax(160px, 0.55fr)) 0.9fr",
            },
            gap: { xs: 3, md: 4 },
            alignItems: "start",
          }}
        >
          <Box>
            <Box
              component={Link}
              to="/"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.25,
                textDecoration: "none",
              }}
            >
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "14px",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "var(--toeic-paper-grid)",
                  boxShadow: "var(--toeic-shadow-card)",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src="/logo-removebg.png"
                  alt="Lingualica"
                  sx={{ width: 40, height: 40, objectFit: "contain" }}
                />
              </Box>
              <Box>
                <Typography sx={{ color: "var(--toeic-ink)", fontWeight: 700, fontSize: 20 }}>
                  Lingualica
                </Typography>
                <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 13, fontWeight: 500 }}>
                  Learn, review, improve.
                </Typography>
              </Box>
            </Box>

            <Typography sx={{ mt: 2, color: "var(--toeic-ink-soft)", lineHeight: 1.7, maxWidth: 420, fontWeight: 500 }}>
              A non-profit English learning space for vocabulary review, exam
              practice, study resources, and stronger daily learning habits.
            </Typography>

            <Box sx={{ mt: 2, display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              <Chip size="small" label="Flashcards" sx={{ bgcolor: "#f0ecff", color: "#6d4aff", fontWeight: 700 }} />
              <Chip size="small" label="IELTS" sx={{ bgcolor: "#e6f8f2", color: "#27866d", fontWeight: 700 }} />
              <Chip size="small" label="TOEIC" sx={{ bgcolor: "#e8f6ff", color: "var(--toeic-sky-deep)", fontWeight: 700 }} />
            </Box>
          </Box>

          {footerGroups.map((group) => (
            <Box key={group.title}>
              <Typography sx={{ color: "var(--toeic-ink)", fontWeight: 700, mb: 1.5 }}>
                {group.title}
              </Typography>
              <Box sx={{ display: "grid", gap: 1 }}>
                {group.links.map((item) => {
                  const Icon = item.icon;

                  return (
                    <Button
                      key={item.label}
                      component={Link}
                      to={item.href}
                      startIcon={<Icon />}
                      sx={{
                        justifyContent: "flex-start",
                        px: 0,
                        py: 0.4,
                        minHeight: 32,
                        borderRadius: "999px",
                        color: "var(--toeic-ink-soft)",
                        fontWeight: 500,
                        "& .MuiButton-startIcon": { mr: 0.9 },
                        "&:hover": {
                          color: "var(--toeic-sky-deep)",
                          bgcolor: "transparent",
                          transform: "translateX(2px)",
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          ))}

          <Box
            sx={{
              pl: { xs: 0, md: 2.5 },
              borderLeft: { xs: 0, md: "1.5px dotted var(--toeic-line)" },
            }}
          >
            <Typography sx={{ color: "var(--toeic-ink)", fontWeight: 700 }}>
              Contact
            </Typography>
            <Typography sx={{ mt: 1, color: "var(--toeic-ink-soft)", lineHeight: 1.6, fontWeight: 500 }}>
              Ho Chi Minh City
              <br />
              Version 1.0
            </Typography>
            <MuiLink
              href="mailto:dukelewis2004@gmail.com"
              sx={{
                mt: 1.5,
                display: "inline-flex",
                color: "var(--toeic-sky-deep)",
                fontWeight: 700,
                textDecoration: "none",
                borderBottom: "1.5px dotted rgba(70, 168, 232, 0.45)",
              }}
            >
              dukelewis2004@gmail.com
            </MuiLink>
            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              {socialLinks.map((social) => {
                const Icon = social.icon;

                return (
                  <IconButton
                    key={social.label}
                    component="a"
                    href={social.href}
                    target={social.href.startsWith("http") ? "_blank" : undefined}
                    rel={social.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    aria-label={social.label}
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "999px",
                      bgcolor: social.bg,
                      color: social.color,
                      boxShadow: "0 8px 18px rgba(35, 135, 204, 0.08)",
                      "&:hover": { bgcolor: social.bg, transform: "translateY(-1px)" },
                    }}
                  >
                    <Icon fontSize="small" />
                  </IconButton>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Box aria-hidden sx={{ my: 3, borderTop: "1.5px dotted var(--toeic-line)" }} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ color: "var(--toeic-ink)", fontSize: 13, fontWeight: 700 }}>
              Lingualica by Duke Lewis
            </Typography>
            <Typography sx={{ mt: 0.4, color: "var(--toeic-ink-soft)", fontSize: 13, fontWeight: 500 }}>
              Lingualica is for educational practice and self-study workflows.
            </Typography>
          </Box>
          <Box
            component="nav"
            aria-label="Duke Lewis network"
            sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}
          >
            {dukeLewisLinks.map((link) => (
              <MuiLink
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "var(--toeic-sky-deep)",
                  fontSize: 13,
                  fontWeight: 700,
                  textDecoration: "none",
                  borderBottom: "1.5px dotted rgba(70, 168, 232, 0.45)",
                  "&:hover": {
                    color: "#6d4aff",
                    borderBottomColor: "rgba(109, 74, 255, 0.55)",
                  },
                }}
              >
                {link.label}
              </MuiLink>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Footer;
